/**
 * The alert rule form (New / Edit alert rule, both apps): plain questions, a sentence on top that
 * says what the rule will do, and at the bottom the rule EXACTLY as it will be saved — with Edit
 * for the few things the questions do not cover.
 *
 *   1  Name                          first, so everything below reads as "this rule"
 *   2  Which event?                  two boxes: the MAIN event (pick one, or Other — type it) with
 *                                    the names it is sent under, editable; and, if wanted, the SUB
 *                                    event with its own names — both must match, or either one
 *                                    (event_type · sub_event_type · event_match, CC migration 067)
 *   3  When should it alert?         every time it is seen, or only when the event's values match
 *                                    — [value name] [test] [value] rows — and, optionally, the AI
 *                                    is X% sure (RuleWhen). No timing: alerts come from values.
 *   4  How serious is it?
 *   5  Don't repeat it for…
 *   6  Alert message                 a suggestion for the event, variables to click in, a preview
 *   The exact rule                   every stored value, read-only; Edit opens the fallback text
 *                                    and the webhook
 *
 * A rule only decides WHETHER something is an alert. Which cameras, who is told and how (alert
 * board, Dashboard pop-up, email, WhatsApp) is Routing's. A rule saved before that may still be
 * limited to some cameras: the form says so and offers to cover every camera.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { LinkIcon, PencilSquareIcon, PlusIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { aaInputCls, aaLabelCls } from '../alertActions/AlertActionsControls';
import { parseEventTypeTokens, type EventTypeOption } from './EventTypeFields';
import RuleWhen, { whenSentence } from './RuleWhen';
import { fieldsFor, BUILTIN_FIELDS, type EventCatalogEntry } from './ruleConditionsModel';
import {
  GENERIC_TEMPLATE, MESSAGE_VARIABLES, eventChoices, inferChoice, previewMessage, spellings, uniqueNames,
  type EventChoice, type EventFamily,
} from './eventAliases';

export interface AlertRuleFormProps {
  form: Record<string, any>;
  setForm: (next: Record<string, any>) => void;
  cameras: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  catalog: EventCatalogEntry[];
  /** The app's built-in detection list — kept for callers; the form uses eventAliases. */
  builtin?: EventTypeOption[];
  /** Where cameras, people and channels are set — the Routing tab. */
  routingHref?: string;
}

const COOLDOWNS = [
  { value: 0, label: 'Every time it is seen' },
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 3600, label: '1 hour' },
];

const SEVERITIES = [
  { value: '*', label: 'Let the system decide', hint: 'Fire, falls and intrusions are critical; the rest are warnings' },
  { value: 'critical', label: 'Critical', hint: 'Needs action now' },
  { value: 'warning', label: 'Warning', hint: 'Someone should look' },
  { value: 'normal', label: 'For information', hint: 'Nothing to do' },
];
const SEV_DOT: Record<string, string> = { critical: 'bg-destructive', warning: 'bg-warning', normal: 'bg-info', '*': 'bg-muted-foreground' };
const OTHER = '__other__';
const splitNames = (v: unknown) => String(v ?? '').split(',').map((t) => t.trim()).filter((t) => t && t !== '*');

function Step({ n, title, hint, children }: { n: number; title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-semibold text-white">{n}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <h4 className="text-[14px] font-semibold text-foreground">{title}</h4>
          {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

/** The names the rule matches — chips with ✕, and a box to add one (with its other spellings). */
function NameChips({ names, onChange, empty = 'No name yet.', placeholder = 'Add another name, e.g. fire_catch' }: {
  names: string[]; onChange: (next: string[]) => void; empty?: string; placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange(uniqueNames([...names, ...spellings(draft)]));
    setDraft('');
  };
  return (
    <div className="rounded-lg border border-border p-2">
      <div className="flex flex-wrap gap-1.5">
        {names.length === 0 && <span className="px-1 text-[12px] text-muted-foreground">{empty}</span>}
        {names.map((n) => (
          <span key={n} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-2.5 pr-1 font-mono text-[12px] text-foreground">
            {n}
            <button type="button" onClick={() => onChange(names.filter((x) => x !== n))} className="rounded-full p-0.5 hover:bg-background" aria-label={`Remove ${n}`}>
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex max-w-md gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className={`${aaInputCls} mt-0 font-mono`}
        />
        <button type="button" onClick={add} className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border px-2.5 text-[12px] text-foreground hover:bg-muted">
          <PlusIcon className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

/** One of the two event boxes. */
function EventBox({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-xl border border-border bg-card px-3 py-3">
      <p className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

function Row({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 border-b border-border py-1.5 last:border-0">
      <dt className="text-[12px] text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-[13px] text-foreground">{children}</dd>
    </div>
  );
}

export default function AlertRuleForm({ form, setForm, cameras, groups, catalog, routingHref }: AlertRuleFormProps) {
  const set = (patch: Record<string, any>) => setForm({ ...form, ...patch });

  // The main event's names (event_type) and, when considered, the sub event's (sub_event_type).
  const mainNames = parseEventTypeTokens(form.event_type).filter((t) => t !== '*');
  const setMainNames = (next: string[]) => set({ event_type: next.length ? uniqueNames(next).join(',') : '*' });
  const subNames = splitNames(form.sub_event_type);
  const setSubNames = (next: string[]) => set({ sub_event_type: uniqueNames(next).join(',') });
  const [subOn, setSubOn] = useState(subNames.length > 0);
  const match: 'all' | 'any' = form.event_match === 'any' ? 'any' : 'all';
  // Every name the rule matches — which fields the AI sends with it (tests, message variables).
  const names = [...mainNames, ...(subOn ? subNames : [])];

  const families = useMemo(() => eventChoices(catalog || []), [catalog]);
  // What the boxes show: what was picked here, else what the rule's names point at.
  const inferredMain = useMemo(() => inferChoice(mainNames, families), [mainNames.join(','), families]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mainSel, setMainSel] = useState<string | null>(null);
  const main = mainSel ?? (inferredMain.family?.value || OTHER);
  const family: EventFamily | null = families.find((f) => f.value === main) || null;
  const inferredSub = useMemo(() => (family ? inferChoice(subNames, [family]).sub : null), [subNames.join(','), family]); // eslint-disable-line react-hooks/exhaustive-deps
  const [subSel, setSubSel] = useState<string | null>(null);
  const sub = subSel ?? (inferredSub?.value || (subNames.length ? OTHER : ''));
  const subChoice: EventChoice | null = family?.subs.find((x) => x.value === sub) || null;
  // The rule's name follows the picks while it is still one of the event names (not typed).
  const renamable = !String(form.name || '').trim() || families.some((f) => f.label === form.name || f.subs.some((x) => x.label === form.name));

  const pickMain = (v: string) => {
    setMainSel(v);
    setSubSel(null);
    const f = families.find((x) => x.value === v);
    set({
      event_type: f ? uniqueNames(f.aliases).join(',') : '*',
      sub_event_type: '',
      ...(f && renamable ? { name: f.label } : {}),
      ...(f && !String(form.message_template || '').trim() ? { message_template: f.template } : {}),
    });
  };
  const pickSub = (v: string) => {
    setSubSel(v);
    const x = family?.subs.find((y) => y.value === v);
    set({ sub_event_type: x ? uniqueNames(x.aliases).join(',') : '', ...(x && renamable ? { name: x.label } : {}) });
  };
  const toggleSub = (on: boolean) => {
    setSubOn(on);
    setSubSel(null);
    if (!on) {
      set({ sub_event_type: '', ...(renamable && family ? { name: family.label } : {}) });
    } else if (family && inferredMain.sub) {
      // A rule from before main / sub were apart keeps a SUB event's names as its event names:
      // move them to the sub box and give the main box the main event's own names — else "both
      // must match" would compare fire_detected with the main event and never fire.
      setSubSel(inferredMain.sub.value);
      set({ event_type: uniqueNames(family.aliases).join(','), sub_event_type: uniqueNames(inferredMain.sub.aliases).join(',') });
    } else if (family?.subs.length === 1) {
      pickSub(family.subs[0].value);
    }
  };

  // An older rule limited to some cameras (cameras are Routing's now) — shown, and removable.
  const camName = (id: string) => cameras.find((c) => c.id === id)?.name || 'a removed camera';
  const groupName = groups.find((g) => g.id === form.group_id)?.name;
  const listed: string[] = Array.isArray(form.camera_ids) ? form.camera_ids : [];
  const oldLimit = listed.length ? listed.map(camName).join(', ')
    : form.camera_id ? camName(form.camera_id)
      : form.group_id ? `cameras in ${groupName || 'a removed group'}` : '';

  const pct = Math.round((parseFloat(form.min_confidence) || 0) * 100);
  // 0 = confidence not checked (the command-center applies no threshold).
  const usesConfidence = pct > 0;
  const cooldown = Number(form.cooldown_seconds) || 0;
  const severity = form.severity || '*';

  // Message
  const msgRef = useRef<HTMLTextAreaElement | null>(null);
  const eventFields = useMemo(
    () => Object.keys(fieldsFor(catalog || [], names)).filter((f) => !(f in BUILTIN_FIELDS)).slice(0, 12),
    [catalog, names.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const insertVar = (v: string) => {
    const el = msgRef.current;
    const text = String(form.message_template || '');
    const token = `\${${v}}`;
    const at = el ? el.selectionStart ?? text.length : text.length;
    const end = el ? el.selectionEnd ?? at : at;
    set({ message_template: `${text.slice(0, at)}${token}${text.slice(end)}`.slice(0, 500) });
    requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = at + token.length; } });
  };
  const suggested = family?.template || GENERIC_TEMPLATE;

  const [editExact, setEditExact] = useState(false);

  // The sentence
  const mainLabel = family?.label || (mainNames.length ? mainNames.slice(0, 2).join(' or ') : 'any event');
  const subLabel = subOn ? subChoice?.label || (subNames.length ? subNames.slice(0, 2).join(' or ') : '') : '';
  const whatText = subLabel ? (match === 'any' ? `${mainLabel} or ${subLabel}` : `${subLabel} (${mainLabel})`) : mainLabel;
  const sevText = severity === '*' ? 'an alert' : severity === 'normal' ? 'an information alert' : `a ${severity} alert`;
  const cooldownText = cooldown ? ` At most one every ${COOLDOWNS.find((c) => c.value === cooldown)?.label || `${cooldown} seconds`} per camera.` : '';
  const when = whenSentence(form);

  return (
    <div className="space-y-5 p-5">
      <p className="rounded-xl border border-border bg-muted px-3 py-2.5 text-[13px] leading-relaxed text-foreground">
        <b>{form.name || 'This rule'}</b>: raise <b>{sevText}</b> when <b>{whatText}</b> is seen{oldLimit && <> on <b>{oldLimit}</b></>}
        {when && <> and <b>{when}</b></>}
        {usesConfidence ? <>, if the AI is at least <b>{pct}%</b> sure.</> : <> — however sure the AI is.</>}{cooldownText}
        {' '}Whether anyone is told — from which cameras, and how — is set under {routingHref ? <a href={routingHref} className="font-medium text-brand hover:underline">Who gets notified</a> : '“Who gets notified”'}.
      </p>

      {oldLimit && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500 px-3 py-2.5 text-[13px] text-foreground">
          <span className="min-w-0 flex-1">
            This rule was saved when rules picked their cameras: it only raises alerts on <b>{oldLimit}</b>.
            Cameras are chosen under “Who gets notified” now — cover every camera here, and choose there who hears about which.
          </span>
          <button type="button" onClick={() => set({ camera_id: null, group_id: null, camera_ids: null })}
            className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted">
            Cover all cameras
          </button>
        </div>
      )}

      {form.websocket_enabled === false && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500 px-3 py-2.5 text-[13px] text-foreground">
          <span className="min-w-0 flex-1">
            This rule's alerts are kept <b>off the alert board</b> (an older setting). The alert board is set on each notification under “Who gets notified” now.
          </span>
          <button type="button" onClick={() => set({ websocket_enabled: true })}
            className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted">
            Let “Who gets notified” decide
          </button>
        </div>
      )}

      <Step n={1} title="Name of the rule" hint="Shown on every alert this rule raises.">
        <input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} className={`${aaInputCls} max-w-md`} placeholder="e.g. Fire in the warehouse" />
      </Step>

      <Step n={2} title="Which event?" hint="What the AI sends. Under each box are the names it may be sent as — add yours if your model uses another.">
        <div className="max-w-2xl space-y-2">
          <EventBox title="Main event">
            <select value={main} onChange={(e) => pickMain(e.target.value)} className={`${aaInputCls} mt-0 max-w-sm`} aria-label="Main event">
              {families.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              <option value={OTHER}>Other — type the event's name</option>
            </select>
            <div>
              <p className={aaLabelCls}>Matches events named</p>
              <NameChips names={mainNames} onChange={setMainNames}
                empty={main === OTHER ? 'No name — any event the AI sends. Type one to narrow it.' : 'No name yet.'}
                placeholder={main === OTHER ? 'Type the event name, e.g. belt_movement' : 'Add another name, e.g. fire_catch'} />
            </div>
          </EventBox>

          <EventBox title="Sub event">
            <label className="flex cursor-pointer items-start gap-2.5">
              <input type="checkbox" checked={subOn} onChange={(e) => toggleSub(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand" />
              <span>
                <span className="block text-[13px] font-medium text-foreground">Also check the sub event</span>
                <span className="block text-[12px] text-muted-foreground">One kind of the main event — e.g. “Fire detected” of Fire &amp; smoke. Off: the sub event is not looked at.</span>
              </span>
            </label>
            {subOn && (
              <div className="space-y-2 pl-[1.6rem]">
                <select value={sub} onChange={(e) => pickSub(e.target.value)} className={`${aaInputCls} mt-0 max-w-sm`} aria-label="Sub event">
                  <option value="" disabled>Pick a sub event…</option>
                  {(family?.subs || []).map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                  <option value={OTHER}>Other — type the sub event's name</option>
                </select>
                <div>
                  <p className={aaLabelCls}>Matches sub events named</p>
                  <NameChips names={subNames} onChange={setSubNames} empty="No name yet — pick one above or type it." placeholder="Type the sub event name, e.g. fire_detect" />
                </div>
                <div>
                  <p className={aaLabelCls}>Alert when</p>
                  <div className="flex flex-wrap gap-1.5">
                    {([['all', 'Both match — main AND sub event'], ['any', 'Either one matches — main OR sub event']] as const).map(([v, label]) => (
                      <button key={v} type="button" onClick={() => set({ event_match: v })} aria-pressed={match === v}
                        className={`rounded-lg border px-3 py-1.5 text-[13px] ${match === v ? 'border-brand bg-brand-light font-medium text-brand' : 'border-border text-foreground hover:bg-muted'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </EventBox>
          <p className="text-[11px] text-muted-foreground">Capitals, spaces, “-” and “_” don't matter: “Fire-Detect” and “fire detect” are the same name.</p>
        </div>
      </Step>

      <Step n={3} title="When should it alert?">
        <RuleWhen form={form} set={set} catalog={catalog || []} names={names} />
      </Step>

      <Step n={4} title="How serious is it?">
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {SEVERITIES.map((s) => (
            <button key={s.value} type="button" onClick={() => set({ severity: s.value })} aria-pressed={severity === s.value}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left ${severity === s.value ? 'border-brand bg-brand-light' : 'border-border hover:bg-muted'}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEV_DOT[s.value]}`} />
              <span>
                <span className="block text-[13px] font-medium text-foreground">{s.label}</span>
                <span className="block text-[11px] text-muted-foreground">{s.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </Step>

      <Step n={5} title="Don't repeat it for…" hint="After an alert, the same event on the same camera is not alerted again until this time has passed.">
        <select value={COOLDOWNS.some((c) => c.value === cooldown) ? cooldown : 'custom'} onChange={(e) => set({ cooldown_seconds: Number(e.target.value) })} className={`${aaInputCls} max-w-xs`}>
          {COOLDOWNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          {!COOLDOWNS.some((c) => c.value === cooldown) && <option value="custom" disabled>{cooldown} seconds</option>}
        </select>
      </Step>

      <Step n={6} title="Alert message" hint="What the alert says on the board, in emails and on WhatsApp. Click a variable to put it where the cursor is.">
        <textarea
          ref={msgRef}
          rows={3}
          maxLength={500}
          value={form.message_template || ''}
          onChange={(e) => set({ message_template: e.target.value })}
          placeholder={suggested}
          className={`${aaInputCls} max-w-2xl resize-y`}
        />
        <div className="flex max-w-2xl flex-wrap items-center gap-1.5">
          {form.message_template !== suggested && (
            <button type="button" onClick={() => set({ message_template: suggested })} className="rounded-lg border border-dashed border-brand px-2.5 py-1 text-[12px] font-medium text-brand hover:bg-brand-light">
              Use the suggested message{family ? ` for ${family.label}` : ''}
            </button>
          )}
          {MESSAGE_VARIABLES.map((v) => (
            <button key={v.name} type="button" onClick={() => insertVar(v.name)} title={`${v.label}, e.g. ${v.sample}`}
              className="rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-foreground hover:bg-muted">
              {`\${${v.name}}`}
            </button>
          ))}
          {eventFields.map((f) => (
            <button key={f} type="button" onClick={() => insertVar(f)} title="Sent by the AI with this event"
              className="rounded-full border border-dashed border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted">
              {`\${${f}}`}
            </button>
          ))}
        </div>
        <div className="max-w-2xl rounded-lg border border-border px-3 py-2">
          <p className={aaLabelCls}>How it will read</p>
          <p className="text-[13px] text-foreground">{previewMessage(form.message_template || suggested, { alert_name: form.name || subChoice?.label || family?.label || 'Alert' })}</p>
        </div>
      </Step>

      <section className="rounded-xl border border-border">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <h4 className="min-w-0 flex-1 text-[14px] font-semibold text-foreground">The rule, exactly as it will be saved</h4>
          <button type="button" onClick={() => setEditExact((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
            {editExact ? <><CheckIcon className="h-4 w-4" /> Done</> : <><PencilSquareIcon className="h-4 w-4" /> Edit</>}
          </button>
        </div>
        <dl className="px-4 py-2">
          <Row k="Name">{form.name || <span className="text-muted-foreground">(none — the event's name is used)</span>}</Row>
          <Row k="Main event names"><span className="font-mono text-[12px]">{mainNames.length ? mainNames.join(', ') : '* (any event)'}</span></Row>
          <Row k="Sub event names">{subOn && subNames.length ? <span className="font-mono text-[12px]">{subNames.join(', ')}</span> : 'Not checked'}</Row>
          {subOn && subNames.length > 0 && <Row k="Main and sub">{match === 'any' ? 'Either one matches' : 'Both must match'}</Row>}
          {oldLimit && <Row k="Cameras">Only {oldLimit} (older rule)</Row>}
          <Row k="Only when">{when || 'Every time it is seen'}</Row>
          <Row k="AI confidence">{usesConfidence ? `At least ${pct}% sure` : 'Not checked'}</Row>
          <Row k="Severity">{SEVERITIES.find((s) => s.value === severity)?.label}</Row>
          <Row k="Repeat">{COOLDOWNS.find((c) => c.value === cooldown)?.label || `every ${cooldown} s`}</Row>
          <Row k="Message"><span className="font-mono text-[12px]">{form.message_template || '(the event\'s own text)'}</span></Row>
          {form.description && <Row k="Fallback text">{form.description}</Row>}
          <Row k="Webhook">{form.webhook_enabled ? <span className="font-mono text-[12px]">{form.webhook_url || '(no address yet)'}</span> : 'Off'}</Row>
        </dl>
        {editExact && (
          <div className="space-y-4 border-t border-border px-4 py-3">
            <label className="block max-w-2xl">
              <span className={aaLabelCls}>Fallback text (used when there is no message above and the AI sends none)</span>
              <input value={form.description || ''} onChange={(e) => set({ description: e.target.value })} className={aaInputCls} />
            </label>
            <div>
              <label className="flex items-center gap-2 text-[13px] text-foreground">
                <input type="checkbox" checked={!!form.webhook_enabled} onChange={(e) => set({ webhook_enabled: e.target.checked })} className="accent-brand" />
                <LinkIcon className="h-4 w-4 text-muted-foreground" /> Also send it to another system (webhook)
              </label>
              {form.webhook_enabled && (
                <input value={form.webhook_url || ''} onChange={(e) => set({ webhook_url: e.target.value })} className={`${aaInputCls} mt-1.5 max-w-2xl`} placeholder="https://…" />
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
